/**
 * MeineAbrechnung.jsx — AP3+AP4 (Mitarbeiter-Seite)
 * Route: /meine-abrechnung
 * Zeigt nur freigegebene Monate mit aufklappbaren Tagen.
 */
import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DashboardLayout } from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ChevronDown, ChevronUp, Loader2, TrendingDown, CheckCircle2, FileText,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;
const authH = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const fmtEur = (v) => new Intl.NumberFormat('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(v) || 0) + '\u00a0€';
const fmtMonat = (m) => {
  const [y, mo] = m.split('-');
  return new Date(Number(y), Number(mo) - 1).toLocaleDateString('de-DE', { month: 'long', year: 'numeric' });
};

export default function MeineAbrechnung() {
  const { user } = useAuth();
  const [locations, setLocations] = useState([]);
  const [selectedLoc, setSelectedLoc] = useState('');
  const [monate, setMonate] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [searchParams] = useSearchParams();
  const monatParam = searchParams.get('monat');
  const monatOpened = useRef(false);

  // Aus Push-/Glocken-Benachrichtigung: passenden Monat automatisch aufklappen
  useEffect(() => {
    if (!monatParam || monatOpened.current || !monate.length) return;
    if (monate.some(m => m.monat === monatParam)) {
      monatOpened.current = true;
      setExpanded(e => ({ ...e, [monatParam]: true }));
      setTimeout(() => {
        document.querySelector(`[data-testid="abrechnung-${monatParam}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 300);
    }
  }, [monatParam, monate]);

  useEffect(() => {
    axios.get(`${API}/api/user/locations`, { headers: authH() })
      .then(r => { setLocations(r.data || []); if (r.data?.[0]) setSelectedLoc(r.data[0].id); })
      .catch(() => {});
  }, []);

  useEffect(() => { if (selectedLoc) fetchData(); }, [selectedLoc]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/kassensturz/meine-abrechnung`, {
        params: { location_id: selectedLoc },
        headers: authH(),
      });
      setMonate(r.data || []);
    } catch { toast.error('Fehler beim Laden'); }
    finally { setLoading(false); }
  };

  return (
    <DashboardLayout>
      <div className="max-w-2xl mx-auto px-4 py-6 space-y-5">
        <div className="flex flex-wrap items-center gap-3 justify-between">
          <h1 className="text-xl font-bold flex items-center gap-2">
            <TrendingDown className="h-5 w-5 text-primary" />
            Meine Abrechnung
          </h1>
          {locations.length > 1 && (
            <select value={selectedLoc} onChange={e => setSelectedLoc(e.target.value)}
              className="rounded-xl border px-3 py-2 text-sm bg-background">
              {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select>
          )}
        </div>

        {loading && (
          <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
        )}

        {!loading && monate.length === 0 && (
          <div className="text-center py-14 space-y-2 text-muted-foreground">
            <CheckCircle2 className="h-10 w-10 mx-auto opacity-30" />
            <p className="text-sm font-medium">Keine freigegebenen Abrechnungen</p>
            <p className="text-xs">Sobald dein Arbeitgeber eine Abrechnung freigibt, erscheint sie hier.</p>
          </div>
        )}

        {monate.map(mo => (
          <Card key={mo.monat} className="shadow-sm" data-testid={`abrechnung-${mo.monat}`}>
            <CardHeader className="pb-2 cursor-pointer select-none"
              onClick={() => setExpanded(e => ({ ...e, [mo.monat]: !e[mo.monat] }))}>
              <CardTitle className="text-base flex items-center justify-between gap-2">
                <div>
                  <p className="font-semibold">{fmtMonat(mo.monat)}</p>
                  <p className="text-xs text-muted-foreground font-normal">
                    Freigegeben am {new Date(mo.freiggegeben_at).toLocaleDateString('de-DE')}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  {mo.gesamt_abzug > 0 ? (
                    <div className="text-right">
                      <p className="text-sm text-red-600 font-bold tabular-nums">−{fmtEur(mo.gesamt_abzug)}</p>
                      <p className="text-[10px] text-muted-foreground">Lohnabzug</p>
                    </div>
                  ) : (
                    <CheckCircle2 className="h-4 w-4 text-green-500" />
                  )}
                  {expanded[mo.monat] ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                </div>
              </CardTitle>
            </CardHeader>

            {expanded[mo.monat] && (
              <CardContent className="space-y-2">
                {(() => {
                  const abzugSchichten = (mo.schichten || []).filter(s => s.lohnabzug_betrag);
                  const hatEintraege = abzugSchichten.length > 0 || (mo.manuelle_abzuege?.length > 0);
                  if (!hatEintraege) {
                    return (
                      <div className="text-center py-6 text-sm text-muted-foreground flex flex-col items-center gap-1">
                        <CheckCircle2 className="h-6 w-6 text-green-500/70" />
                        Keine Lohnabzüge in diesem Monat
                      </div>
                    );
                  }
                  return null;
                })()}

                {/* Nur final festgelegte Lohnabzüge aus Schichten (keine Kasse/Soll/Differenz-Rohdaten) */}
                {(mo.schichten || []).filter(s => s.lohnabzug_betrag).map(s => (
                  <div key={s.id} className="rounded-xl border border-amber-200 bg-amber-50/40 dark:bg-amber-900/10 px-3 py-2.5 space-y-1"
                    data-testid={`meine-lohnabzug-${s.id}`}>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-muted-foreground w-20 shrink-0">
                        {new Date(s.datum).toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit' })}
                      </span>
                      <span className="text-sm flex-1">{s.schicht_name}</span>
                      <span className="font-mono text-sm font-semibold tabular-nums text-red-600">
                        −{fmtEur(s.lohnabzug_betrag)}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 ml-20 text-xs">
                      <span className={s.lohnabzug_erledigt ? 'text-green-600 font-medium' : 'text-amber-700 font-medium'}>
                        {s.lohnabzug_erledigt ? 'Beglichen ✓' : 'Ausstehend'}
                      </span>
                      {s.foto_url && (
                        <a href={s.foto_url} target="_blank" rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-primary hover:underline"
                          data-testid={`meine-zbericht-${s.id}`}>
                          <FileText className="h-3.5 w-3.5" /> Z-Bericht ansehen
                        </a>
                      )}
                    </div>
                  </div>
                ))}

                {/* Manuelle/Weitere Abzüge */}
                {mo.manuelle_abzuege?.length > 0 && (
                  <div className="space-y-1.5 pt-1">
                    <p className="text-xs font-semibold text-muted-foreground">Weitere Abzüge</p>
                    {mo.manuelle_abzuege.map(a => (
                      <div key={a.id} className="rounded-xl border border-amber-200 bg-amber-50/40 dark:bg-amber-900/10 px-3 py-2.5"
                        data-testid={`meine-abzug-${a.id}`}>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-muted-foreground w-20 shrink-0">
                            {new Date(a.datum).toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit' })}
                          </span>
                          <span className="text-sm flex-1">{a.grund || 'Manueller Abzug'}</span>
                          <span className="font-mono text-sm font-semibold tabular-nums text-red-600">
                            −{fmtEur(a.betrag)}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Monats-Summe: nur der tatsächlich geschuldete Lohnabzug */}
                {mo.gesamt_abzug > 0 && (
                  <div className="flex items-center justify-between border-t pt-2 px-1">
                    <span className="text-sm font-semibold">Lohnabzug gesamt</span>
                    <p className="text-sm font-bold text-red-600 tabular-nums" data-testid={`meine-summe-${mo.monat}`}>
                      −{fmtEur(mo.gesamt_abzug)}
                    </p>
                  </div>
                )}
              </CardContent>
            )}
          </Card>
        ))}
      </div>
    </DashboardLayout>
  );
}
